"use client";

import { getExternalSocialAuthenticationUrl, setUserFlowCheckout } from "@/api/auth";
import { continueGuestCheckout } from "@/api/saleor/services/checkout";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import {
  createGaUserObject,
  createUserJourneyAnalyticsPayload,
  handleNewsLetterSubscriptionAnalytics,
  localAsset,
} from "@/apiUtils/commonUtils";
import {
  adb2cCustomPolicies,
  analyticEventTypes,
  analyticKeyIds,
  sessionFlowConstants,
} from "@/apiUtils/constants";
import { getLocalStorage, removeLocalStorage } from "@/apiUtils/localStorageUtils";
import { getCheckoutResumePath } from "@/utils/checkoutResumeUtils";
import CloseIcon from "@/assets/icons/close.svg?react";
import { TranslationData, TypeFederationAuthProvider, TypeSessionUser } from "@/common/lib/types";
import useRouter from "@/hooks/useCRouter";
import { getClientCookie } from "@/utils/common";
import Image from "next/image";
import { FC, memo, Suspense, useEffect, useRef, useState } from "react";
import OIDCFrame from "../OIDC/OIDCFrame";

interface SignInSignUpModalProps {
  translations: TranslationData;
  show: boolean;
  tabs: string[];
  onClose: () => void;
  activeTab: string;
  onLoaded: () => void;
  disableRedirect: boolean | null;
  isCheckoutFlow?: boolean;
}

interface TypeSocialLoginIconProps {
  provider: TypeFederationAuthProvider;
}

export const SocialLoginIcon: FC<TypeSocialLoginIconProps> = (props) => {
  const { provider } = props;

  const [loading, setLoading] = useState(false);
  const handleSocialLoginButtonClick = async () => {
    if (provider) {
      try {
        setLoading(true);
        const response = await getExternalSocialAuthenticationUrl(provider);

        if (response) {
          // TODO : R & D on social login iframe popup
          // const response = window.open(provider.url, provider.provider, "popup=yes");
          window.location.href = response;
        }
      } catch (error) {}
      setLoading(false);
    }
  };

  return (
    <button
      className="btn btn-social w-100"
      onClick={handleSocialLoginButtonClick}
      style={
        loading
          ? {
              cursor: "wait",
            }
          : {}
      }
    >
      <Image
        src={localAsset(`images/social-media/${provider}.svg`)}
        width={28}
        height={28}
        alt={provider}
      />
    </button>
  );
};

const SignInSignUpModal: FC<SignInSignUpModalProps> = memo((props) => {
  const { show: defaultShow, onClose, onLoaded, disableRedirect, isCheckoutFlow } = props;

  // This internal state is a work around to maintain slide in/out animation of dynamic imported component
  const [show, setShow] = useState(false);

  const onLoadedRef = useRef(onLoaded);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [iframeHeight, setIframeHeight] = useState(0);

  const { nextPush, push } = useRouter();

  // const {} = useCookies()
  const localeCookie = getClientCookie("NEXT_LOCALE");
  const locale = localeCookie?.split("-")[1] || "en";
  const localeAndRegion = localeCookie || "sa-en";
  const region = (localeCookie?.split("-")[0] || "sa").toUpperCase();

  const defaultCheckout = "/checkout";

  useEffect(() => {
    onLoadedRef.current?.();
  }, []);

  useEffect(() => {
    setIframeHeight(window.innerHeight - (headerRef.current?.clientHeight || 0));
  }, [show]);

  useEffect(() => {
    setShow(defaultShow);
  }, [defaultShow]);

  useEffect(() => {
    if (show) {
      setUserFlowCheckout(!!isCheckoutFlow);
    }
  }, [show, isCheckoutFlow]);

  useEffect(() => {
    window.document.body.classList.toggle("modal-open", show);
    window.document.body.style.overflow = show ? "hidden" : "";
    window.document.body.style.paddingRight = show ? "15px" : "";
    return () => {
      window.document.body.classList.remove("modal-open");
      window.document.body.style.overflow = "";
      window.document.body.style.paddingRight = "";
    };
  }, [show]);

  const handleRedirect = (url: string) => {
    if (disableRedirect) {
      return;
    }
    if (isCheckoutFlow) {
      let redirectPathname = url;
      try {
        redirectPathname = new URL(url).pathname;
      } catch (error) {}
      try {
        redirectPathname = redirectPathname?.replace(new RegExp(`^/${locale}`), "");
      } catch (error) {}

      if (new RegExp(`^/(${["checkout"].join("|")}).*`).test(redirectPathname)) {
        nextPush(url);
      } else {
        push(defaultCheckout);
      }
    } else {
      nextPush(url);
    }
  };

  const handleUserJourneyAnalytics = (journey: string, user: TypeSessionUser) => {
    const analyticsPayload = createUserJourneyAnalyticsPayload(journey, user);
    if (analyticsPayload) {
      handlePostAnalytics(analyticsPayload);
    }
  };

  const handleLogin = async (user: TypeSessionUser, redirectUrl: string) => {
    // TODO: Handle user login
    const userJourney = getLocalStorage<string>(analyticKeyIds.analyticUserJourney);
    if (userJourney && user) {
      if (userJourney === sessionFlowConstants.REGISTER) {
        const currentUrl = window.location.href;

        const isSocialLogin = currentUrl.includes("/api/OIDC/federation/");

        let method = "email";
        if (isSocialLogin) {
          const providerMatch = currentUrl.match(/\/api\/OIDC\/federation\/([^/?]+)/);
          if (providerMatch) {
            method = providerMatch[1].replace(".com", "");
          }
        }

        handlePostAnalytics({
          eventType: analyticEventTypes.CUSTOM,
          data: {
            target: analyticEvents.SIGN_UP,
            method: method,
            userData: createGaUserObject(user),
          },
        });
      } else {
        handleUserJourneyAnalytics(userJourney, user);
      }
      handleNewsLetterSubscriptionAnalytics(user, userJourney);
      removeLocalStorage(analyticKeyIds.analyticUserJourney);
    }

    await fetch("/api/auth/post-login", {
      next: {
        revalidate: 30,
      },
    });

    onClose();
    // handleRedirect(
    //   redirectUrl.includes("my-account") ? `/${localeAndRegion}/my-account/dashboard` : redirectUrl,
    // );
  };

  const handleGuestCheckout = async (email: string) => {
    const response = await continueGuestCheckout(email);
    if (response) {
      onClose();
      handleRedirect(`/${localeAndRegion}${getCheckoutResumePath()}`);
    }
  };

  const handleBackdropClick = () => {
    onClose();
  };

  // console.log("socailProviders parent ",socialLoginProviders);

  return (
    <>
      <div
        className={`fixed top-0 h-full transform bg-white transition-transform ${
          show ? "translate-x-0" : "translate-x-full"
        } z-50 w-full max-w-md ${document.documentElement.dir === "rtl" ? "left-0" : "right-0"}`}
        style={{
          visibility: show ? "visible" : "hidden",
        }}
      >
        <div className="p-8 md:p-5" ref={headerRef}>
          <div className="flex justify-between">
            <div className="md:hidden">
              <button className="btn p-0" onClick={onClose}>
                {/* <IconArrowLeft className="icon md arrow" /> */}
              </button>
            </div>
            <div className="ml-auto">
              <button className="p-2 text-gray-500 hover:text-gray-700" onClick={onClose}>
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <Suspense fallback={<></>}>
          <OIDCFrame
            ref={containerRef}
            id="login-email"
            height={"100%"}
            htmlKey="login-emaildd"
            policy={adb2cCustomPolicies.EMAIL_PHONE_SIGNIN_SIGNUP}
            style={{ maxHeight: iframeHeight ? `${iframeHeight}px` : `calc( 100vh - 58px )` }}
            onGuest={isCheckoutFlow && show ? handleGuestCheckout : undefined}
            onSuccess={handleLogin}
            show={show}
            // customOptions={{
            //   countryCode: region,
            //   ui_locales: locale,
            //   env: process.env.NEXT_PUBLIC_ENV
            // }}
          />
        </Suspense>
      </div>
      <div
        className={`fixed inset-0 z-20 bg-black bg-opacity-50 transition-opacity ${show ? "opacity-100" : "pointer-events-none opacity-0"}`}
        onClick={handleBackdropClick}
      />
    </>
  );
});

export default SignInSignUpModal;
